import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  PilotApplication,
  PilotApplicationCounts,
  PilotApplicationEvent,
  PilotApplicationStatus,
} from "@siteagain/shared-types";

const STATUSES: PilotApplicationStatus[] = [
  "new",
  "contacted",
  "call_booked",
  "accepted",
  "declined",
  "archived",
];

const STATUS_LABELS: Record<PilotApplicationStatus, string> = {
  new: "New",
  contacted: "Contacted",
  call_booked: "Call booked",
  accepted: "Accepted",
  declined: "Declined",
  archived: "Archived",
};

const GOAL_LABELS: Record<string, string> = {
  leads: "More qualified enquiries",
  traffic: "More organic discovery",
  maintenance: "Lower maintenance cost",
  other: "Something else",
};

const ELIGIBILITY_LABELS: Record<string, string> = {
  strong: "Strong candidate",
  needs_foundation: "Needs foundation",
  insufficient_data: "Not enough evidence",
};

const SITE_MANAGER_LABELS: Record<string, string> = {
  me: "The applicant",
  developer: "A developer",
  agency: "An agency",
  nobody: "Nobody currently",
};

function yesNoLabel(value: string | null): string {
  if (value === "yes") return "Yes";
  if (value === "no") return "No";
  return "—";
}

/**
 * Pilot waitlist inbox.
 *
 * The queue only ever holds prospects, so every action here is about a person:
 * move the application along, leave an internal note, or send them an email.
 * The trail is rendered from the server's event log — nothing is inferred
 * client-side, so two operators see the same history.
 */
export function PilotInbox({ operatorEmail }: { operatorEmail: string }) {
  const [applications, setApplications] = useState<PilotApplication[]>([]);
  const [counts, setCounts] = useState<PilotApplicationCounts | null>(null);
  const [filter, setFilter] = useState<PilotApplicationStatus | "all">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [events, setEvents] = useState<PilotApplicationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [note, setNote] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const query = filter === "all" ? "" : `?status=${filter}`;
      const data = await pilotApi<{ applications: PilotApplication[]; counts: PilotApplicationCounts }>(
        `/operator/pilot-applications${query}`,
      );
      setApplications(data.applications);
      setCounts(data.counts);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The pilot inbox could not load");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(
    () => applications.find((application) => application.id === selectedId) ?? null,
    [applications, selectedId],
  );

  const loadDetail = useCallback(async (id: string) => {
    try {
      const data = await pilotApi<{ application: PilotApplication; events: PilotApplicationEvent[] }>(
        `/operator/pilot-applications/${id}`,
      );
      setEvents(data.events);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The application could not load");
    }
  }, []);

  useEffect(() => {
    if (!selected) return;
    setNote("");
    setSubject(`Your SiteAgain pilot application — ${selected.website}`);
    setBody(
      `Hi ${selected.name.split(" ")[0] ?? selected.name},\n\n` +
        "Thanks for applying to the SiteAgain pilot. I have looked at " +
        `${selected.website} and here is what stands out:\n\n\n\n` +
        "If that is useful, I can rebuild one page as a private preview so you can compare it against the current one.\n\n" +
        `${operatorEmail}\nSiteAgain`,
    );
    void loadDetail(selected.id);
  }, [selected, operatorEmail, loadDetail]);

  async function run(action: () => Promise<void>, message: string) {
    setBusy(true);
    setError("");
    setFlash("");
    try {
      await action();
      setFlash(message);
      await load(true);
      if (selectedId) await loadDetail(selectedId);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "That action failed");
    } finally {
      setBusy(false);
    }
  }

  const changeStatus = (status: PilotApplicationStatus) =>
    run(async () => {
      if (!selected) return;
      await pilotApi(`/operator/pilot-applications/${selected.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, ownerEmail: selected.ownerEmail ?? operatorEmail }),
      });
    }, `Moved to ${STATUS_LABELS[status]}.`);

  const claim = () =>
    run(async () => {
      if (!selected) return;
      await pilotApi(`/operator/pilot-applications/${selected.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerEmail: operatorEmail }),
      });
    }, "You own this application now.");

  const saveNote = () =>
    run(async () => {
      if (!selected || note.trim().length === 0) return;
      await pilotApi(`/operator/pilot-applications/${selected.id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: note.trim() }),
      });
      setNote("");
    }, "Note saved.");

  const sendEmail = () =>
    run(async () => {
      if (!selected) return;
      await pilotApi(`/operator/pilot-applications/${selected.id}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject, body }),
      });
    }, "Follow-up sent.");

  return (
    <div className="ops-pilot">
      <div className="ops-pilot-toolbar">
        <div className="ops-filters" role="group" aria-label="Filter applications by status">
          <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>
            All <b>{counts?.total ?? 0}</b>
          </button>
          {STATUSES.map((status) => (
            <button key={status} className={filter === status ? "active" : ""} onClick={() => setFilter(status)}>
              {STATUS_LABELS[status]} <b>{counts?.byStatus[status] ?? 0}</b>
            </button>
          ))}
        </div>
        <button className="ops-pilot-refresh" onClick={() => void load()} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && <div className="ops-error" role="alert"><span>{error}</span><button onClick={() => setError("")}>×</button></div>}
      {flash && <div className="ops-pilot-flash" role="status">{flash}</div>}

      <div className="ops-pilot-grid">
        <section className="ops-panel">
          <div className="ops-panel-head">
            <div><small>Pilot waitlist</small><h2>Applications</h2></div>
            <span className="ops-count">{applications.length}</span>
          </div>
          <div className="ops-table-wrap">
            <table className="ops-table ops-pilot-table">
              <thead>
                <tr><th>Applicant</th><th>Site</th><th>Eligibility</th><th>Status</th><th>Applied</th></tr>
              </thead>
              <tbody>
                {applications.map((application) => (
                  <tr
                    key={application.id}
                    className={selectedId === application.id ? "selected" : ""}
                    onClick={() => setSelectedId(application.id)}
                  >
                    <td>
                      <div className="ops-pilot-who">
                        <strong>{application.name}</strong>
                        <small>{application.email}</small>
                      </div>
                    </td>
                    <td><span className="ops-pilot-site">{application.website}</span></td>
                    <td>
                      {application.eligibilityStatus
                        ? ELIGIBILITY_LABELS[application.eligibilityStatus] ?? application.eligibilityStatus
                        : "—"}
                    </td>
                    <td><span className={`ops-status tone-${statusTone(application.status)}`}><i />{STATUS_LABELS[application.status]}</span></td>
                    <td><time dateTime={application.createdAt}>{relativeTime(application.createdAt)}</time></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!loading && applications.length === 0 && (
              <div className="ops-empty"><strong>No applications in this view.</strong></div>
            )}
            {loading && applications.length === 0 && (
              <div className="ops-empty"><strong>Loading applications…</strong></div>
            )}
          </div>
        </section>

        <section className="ops-panel ops-pilot-detail">
          {selected ? (
            <>
              <div className="ops-panel-head">
                <div>
                  <small>Application</small>
                  <h2>{selected.name}</h2>
                </div>
                <span className={`ops-status tone-${statusTone(selected.status)}`}><i />{STATUS_LABELS[selected.status]}</span>
              </div>

              <div className="ops-pilot-body">
                <dl className="ops-pilot-facts">
                  <div><dt>Email</dt><dd><a href={`mailto:${selected.email}`}>{selected.email}</a></dd></div>
                  <div><dt>Site</dt><dd><a href={`https://${selected.website}`} target="_blank" rel="noreferrer">{selected.website}</a></dd></div>
                  <div><dt>Company</dt><dd>{selected.company ?? "—"}</dd></div>
                  <div><dt>Goal</dt><dd>{selected.goal ? GOAL_LABELS[selected.goal] ?? selected.goal : "—"}</dd></div>
                  <div>
                    <dt>Eligibility</dt>
                    <dd>
                      {selected.eligibilityStatus
                        ? ELIGIBILITY_LABELS[selected.eligibilityStatus] ?? selected.eligibilityStatus
                        : "Not classified"}
                      {selected.eligibilityReasons.length > 0 && (
                        <small> ({selected.eligibilityReasons.map((reason) => reason.replaceAll("_", " ")).join(", ")})</small>
                      )}
                    </dd>
                  </div>
                  <div><dt>Audit job</dt><dd>{selected.auditJobId ?? "—"}</dd></div>
                  <div><dt>Next 3 months</dt><dd>{selected.horizonGoal ?? "—"}</dd></div>
                  <div><dt>Who manages it</dt><dd>{selected.siteManager ? SITE_MANAGER_LABELS[selected.siteManager] ?? selected.siteManager : "—"}</dd></div>
                  <div><dt>Search Console access</dt><dd>{yesNoLabel(selected.gscAccess)}</dd></div>
                  <div><dt>Weekly capacity</dt><dd>{yesNoLabel(selected.implementationCapacity)}</dd></div>
                  <div><dt>Locale</dt><dd>{selected.locale.toUpperCase()}</dd></div>
                  <div><dt>Source</dt><dd>{selected.source ?? "—"}</dd></div>
                  <div><dt>Owner</dt><dd>{selected.ownerEmail ?? "Unassigned"}</dd></div>
                  <div><dt>Contacted</dt><dd>{selected.contactCount === 0 ? "Never" : `${selected.contactCount}× · ${relativeTime(selected.lastContactedAt ?? selected.createdAt)}`}</dd></div>
                  <div><dt>Acceptance email</dt><dd>{selected.acceptanceSentAt ? relativeTime(selected.acceptanceSentAt) : "Not sent"}</dd></div>
                </dl>

                {selected.message && (
                  <div className="ops-pilot-message"><small>Their message</small><p>{selected.message}</p></div>
                )}

                <div className="ops-pilot-actions">
                  <label className="ops-pilot-status-select">
                    <span>Status</span>
                    <select value={selected.status} onChange={(event) => void changeStatus(event.target.value as PilotApplicationStatus)} disabled={busy}>
                      {STATUSES.map((status) => <option key={status} value={status}>{STATUS_LABELS[status]}</option>)}
                    </select>
                  </label>
                  {selected.ownerEmail !== operatorEmail && (
                    <button className="ops-pilot-claim" onClick={() => void claim()} disabled={busy}>Assign to me</button>
                  )}
                </div>

                <section className="ops-pilot-trail">
                  <small>History</small>
                  <ul>
                    {events.map((event) => (
                      <li key={event.id}>
                        <span className={`ops-pilot-event kind-${event.kind}`} />
                        <div>
                          <strong>{humanizeEvent(event.kind)}</strong>
                          {event.body && <p>{event.body}</p>}
                          <small>{event.actor ?? "Public"} · {relativeTime(event.createdAt)}</small>
                        </div>
                      </li>
                    ))}
                    {events.length === 0 && <li className="empty"><div><small>No history yet.</small></div></li>}
                  </ul>
                </section>

                <section className="ops-pilot-compose">
                  <small>Internal note</small>
                  <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={2} placeholder="Visible only to operators." />
                  <button onClick={() => void saveNote()} disabled={busy || note.trim().length === 0}>Save note</button>
                </section>

                <section className="ops-pilot-compose">
                  <small>Email the applicant</small>
                  <input value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="Subject" />
                  <textarea value={body} onChange={(event) => setBody(event.target.value)} rows={8} />
                  <button className="send" onClick={() => void sendEmail()} disabled={busy || subject.trim().length === 0 || body.trim().length === 0}>
                    Send follow-up
                  </button>
                </section>
              </div>
            </>
          ) : (
            <div className="ops-empty"><strong>Select an application to work it.</strong></div>
          )}
        </section>
      </div>
    </div>
  );
}

function statusTone(status: PilotApplicationStatus): string {
  if (status === "accepted") return "good";
  if (status === "declined") return "bad";
  if (status === "archived") return "quiet";
  if (status === "new") return "moving";
  return "quiet";
}

function humanizeEvent(kind: string): string {
  return kind.replaceAll("_", " ").replace(/^./, (character) => character.toUpperCase());
}

function relativeTime(value: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

async function pilotApi<T = Record<string, unknown>>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, init);
  const body = (await response.json().catch(() => ({}))) as T & { error?: { message?: string } };
  if (!response.ok) throw new Error(body.error?.message ?? `Request failed (${response.status})`);
  return body;
}
